import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

import { GIFT_PROPERTY as PRIVATE_GIFT_PROPERTY, offerCounts, selectGift } from '@theme/lurexia-bogo-cart';

import { GiftDetails } from './lurexia-bogo-details.js?v=20260923-5';

class LurexiaBogoOffer extends HTMLElement {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLDialogElement | null} */
  #dialog;

  /** @type {Array<Record<string, any>>} */
  #products = [];

  /** @type {Promise<void> | null} */
  #productsPromise = null;

  /** @type {{ productId: number, variantId: number, title: string, vendor: string } | null} */
  #selection = null;

  #details;
  #opening = false;
  #query = '';
  #brand = '';
  #shape = '';
  #returnToCart = false;
  #replacementKey = '';
  #remainingEntitlements = 0;
  #giftMutationInProgress = false;
  #reconcileInProgress = false;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#details = new GiftDetails(this, {
      select: (selection) => { this.#selection = selection; },
      add: () => this.#addSelectedGift(),
      replacing: () => Boolean(this.#replacementKey),
    });
    document.addEventListener('click', this.#handleDocumentClick, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate, { signal });
    this.#dialog.addEventListener('click', this.#handleDialogClick, { signal });
    this.#dialog.addEventListener('cancel', (event) => {
      if (this.#giftMutationInProgress) event.preventDefault();
    }, { signal });
    this.#dialog.addEventListener('close', this.#handleDialogClose, { signal });
    this.querySelector('[data-lurexia-bogo-search]')?.addEventListener('input', this.#handleSearch, { signal });
    this.querySelector('[data-lurexia-bogo-brand]')?.addEventListener('change', this.#handleFilter, { signal });
    this.querySelector('[data-lurexia-bogo-shape]')?.addEventListener('change', this.#handleFilter, { signal });
    this.querySelector('[data-gift-retry]')?.addEventListener('click', () => this.#loadProducts(), { signal });
    this.querySelector('[data-lurexia-bogo-add]')?.addEventListener('click', this.#addSelectedGift, { signal });

    this.#fetchCart()
      .then((cart) => this.#trimExcessGifts(cart))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[lurexia-bogo] Initial cart reconciliation failed:', error);
      });
  }

  disconnectedCallback() {
    this.#details?.destroy();
    this.#abortController.abort();
  }

  #handleDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-lurexia-bogo-open]');
    if (!trigger) return;

    event.preventDefault();
    this.open({
      returnToCart: Boolean(trigger.closest('#cart-drawer, .cart-page')),
      replacementKey: trigger.getAttribute('data-lurexia-bogo-replace-key') || '',
    });
  };

  #handleDialogClick = (event) => {
    if (!(event.target instanceof Element) || !this.#dialog) return;
    if (this.#giftMutationInProgress) return;

    if (event.target === this.#dialog || event.target.closest('[data-lurexia-bogo-close]')) {
      this.#dialog.close();
      return;
    }
  };

  #handleSearch = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    this.#query = event.target.value;
    this.#renderProducts();
  };

  #handleFilter = (event) => {
    if (!(event.target instanceof HTMLSelectElement)) return;
    if (event.target.matches('[data-lurexia-bogo-brand]')) this.#brand = event.target.value;
    if (event.target.matches('[data-lurexia-bogo-shape]')) this.#shape = event.target.value;
    this.#renderProducts();
  };

  #handleCartLinesUpdate = (event) => {
    const source = event.target instanceof Element ? event.target : null;
    const qualifyingAdd =
      event.action === 'add' &&
      Boolean(source?.closest('product-form-component[data-lurexia-bogo-qualifying="true"]'));

    if (qualifyingAdd) {
      event.promise
        ?.then(async ({ detail }) => {
          if (detail?.didError) return;
          const cart = await this.#fetchCart();
          const { remaining } = this.#offerCounts(cart);
          this.#syncClaimWidget(cart);

          if (remaining === 0 || this.dataset.autoOpen === 'false') {
            this.#openCartDrawer();
            return;
          }

          await this.open({ returnToCart: true });
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[lurexia-bogo] Cart add event failed:', error);
        });
      return;
    }

    if (this.#giftMutationInProgress || this.#reconcileInProgress) return;

    event.promise
      ?.then(async ({ detail }) => {
        if (detail?.didError) return;
        const cart = await this.#fetchCart();
        await this.#trimExcessGifts(cart);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[lurexia-bogo] Cart reconciliation failed:', error);
      });
  };

  /** @param {{ returnToCart?: boolean, replacementKey?: string }} [options] */
  async open(options = {}) {
    if (!this.#dialog?.showModal || this.#opening || this.#giftMutationInProgress) return;
    this.#opening = true;
    try {
      this.#returnToCart = true;
      this.#replacementKey = options.replacementKey || '';
      const cart = await this.#fetchCart();
      const counts = this.#offerCounts(cart);
      this.#remainingEntitlements = counts.remaining;
      this.#syncClaimWidget(cart);

      // A gift can consume an entitlement even when Shopify did not discount it.
      // Treat that line as a replacement so the error action can recover instead
      // of refusing to open and returning the customer to the cart drawer.
      if (!this.#replacementKey) {
        const unappliedGift = cart.items.find(
          (item) => item.properties?.[PRIVATE_GIFT_PROPERTY] === 'true' && item.final_line_price > 0
        );
        this.#replacementKey = unappliedGift?.key || '';
      }

      if (this.#remainingEntitlements === 0 && !this.#replacementKey) {
        if (this.#returnToCart) this.#openCartDrawer();
        return;
      }

      const cartDrawer = /** @type {HTMLElement & { isOpen?: boolean, close?: () => void } | null} */ (
        document.querySelector('#cart-drawer')
      );

      if (cartDrawer?.isOpen && typeof cartDrawer.close === 'function') {
        cartDrawer.close();
        await new Promise((resolve) => requestAnimationFrame(resolve));
      }

      this.#clearSelection();
      this.#details.showList();
      this.#dialog.showModal();
      this.#syncClaimWidget(cart);
      await this.#loadProducts();
    } catch (error) {
      this.#announce("We couldn’t load your cart. Please try again.");
      console.warn("[lurexia-bogo] Open failed:", error);
    } finally { this.#opening = false; }
  }

  #handleDialogClose = () => {
    this.#details.showList();
    this.#replacementKey = '';
    this.#fetchCart()
      .then((cart) => this.#syncClaimWidget(cart))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[lurexia-bogo] Claim widget update failed:', error);
      });
    if (!this.#returnToCart) return;
    this.#returnToCart = false;
    requestAnimationFrame(() => this.#openCartDrawer());
  };

  #openCartDrawer() {
    const cartDrawer = /** @type {HTMLElement & { open?: () => void } | null} */ (document.querySelector('#cart-drawer'));
    if (typeof cartDrawer?.open === 'function') cartDrawer.open();
  }

  async #loadProducts() {
    if (this.#products.length > 0) {
      this.#renderProducts();
      return;
    }

    if (this.#productsPromise) return this.#productsPromise;

    const retry = this.querySelector('[data-gift-retry]');
    if (retry) retry.hidden = true;
    this.#setState(this.dataset.loadingLabel || 'Loading free pairs…');
    const handle = this.dataset.freeCollectionHandle;
    if (!handle) return;

    this.#productsPromise = fetch(`${window.Shopify?.routes?.root || '/'}collections/${handle}/products.json?limit=250`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Collection request failed: ${response.status}`);
        return response.json();
      })
      .then((payload) => {
        this.#products = (payload.products || [])
          .map((product) => {
            const variant = product.variants?.find((item) => item.available);
            if (!variant || !product.images?.[0]?.src) return null;
            return {
              id: product.id,
              handle: product.handle,
              title: product.title,
              vendor: product.vendor,
              image: product.images[0].src,
              images: product.images.map(image => image.src),
              variants: product.variants,
              description: product.body_html || "",
              variantId: variant.id,
              variantTitle: variant.title,
              tags: Array.isArray(product.tags) ? product.tags : [],
              shapes: this.#detectShapes(product),
            };
          })
          .filter(Boolean);
        this.#populateFilters();
        this.#renderProducts();
      })
      .catch((error) => {
        if (retry) retry.hidden = false;
        console.error('[lurexia-bogo] Unable to load products:', error);
        this.#setState(this.dataset.errorLabel || "We couldn't load the free pairs. Please try again.");
      })
      .finally(() => {
        this.#productsPromise = null;
      });

    return this.#productsPromise;
  }

  #renderProducts() {
    const grid = this.querySelector('[data-lurexia-bogo-grid]');
    const count = this.querySelector('[data-lurexia-bogo-count]');
    if (!(grid instanceof HTMLElement)) return;

    const normalizedQuery = this.#query.trim().toLocaleLowerCase();
    const products = this.#products.filter((product) => {
      const matchesQuery = `${product.vendor} ${product.title}`.toLocaleLowerCase().includes(normalizedQuery);
      const matchesBrand = !this.#brand || product.vendor === this.#brand;
      const matchesShape = !this.#shape || product.shapes.includes(this.#shape);
      return matchesQuery && matchesBrand && matchesShape;
    });

    if (this.#selection && !products.some((product) => product.variantId === this.#selection?.variantId)) {
      this.#clearSelection();
    }

    grid.replaceChildren(...products.map((product) => this.#details.card(product)));
    grid.hidden = products.length === 0;

    if (count) count.textContent = `${products.length} ${this.dataset.searchResultsLabel || 'pairs available'}`;
    this.#setState(products.length === 0 ? this.dataset.searchEmptyLabel || 'No matching pairs found.' : '');
  }

  #populateFilters() {
    const brandSelect = this.querySelector('[data-lurexia-bogo-brand]');
    const shapeSelect = this.querySelector('[data-lurexia-bogo-shape]');

    if (brandSelect instanceof HTMLSelectElement) {
      const brands = [...new Set(this.#products.map((product) => product.vendor).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      );
      brandSelect.replaceChildren(
        new Option(this.dataset.allBrandsLabel || 'All brands', ''),
        ...brands.map((brand) => new Option(brand, brand))
      );
    }

    if (shapeSelect instanceof HTMLSelectElement) {
      const shapeLabels = new Map(this.#shapeDefinitions().map(({ value, label }) => [value, label]));
      const shapes = [...new Set(this.#products.flatMap((product) => product.shapes))].sort((a, b) =>
        (shapeLabels.get(a) || a).localeCompare(shapeLabels.get(b) || b)
      );
      shapeSelect.replaceChildren(
        new Option(this.dataset.allShapesLabel || 'All shapes', ''),
        ...shapes.map((shape) => new Option(shapeLabels.get(shape) || shape, shape))
      );
    }
  }

  /** @param {Record<string, any>} product */
  #detectShapes(product) {
    const source = `${product.title || ''} ${(product.tags || []).join(' ')}`.toLocaleLowerCase();
    return this.#shapeDefinitions()
      .filter(({ pattern }) => pattern.test(source))
      .map(({ value }) => value);
  }

  #shapeDefinitions() {
    return [
      { value: 'aviator', label: 'Aviator', pattern: /\baviator\b/ },
      { value: 'cat-eye', label: 'Cat-eye', pattern: /\bcat[ -]?eye\b/ },
      { value: 'oval', label: 'Oval', pattern: /\boval\b/ },
      { value: 'rectangular', label: 'Rectangular', pattern: /\brectang(?:le|ular)\b/ },
      { value: 'rimless', label: 'Rimless', pattern: /\brimless\b/ },
      { value: 'round', label: 'Round', pattern: /\bround\b/ },
      { value: 'sport', label: 'Sport', pattern: /\bsport(?:s|y)?\b/ },
      { value: 'square', label: 'Square', pattern: /\bsquare\b/ },
    ];
  }

  #clearSelection() {
    this.#selection = null;
    this.querySelectorAll('[data-lurexia-bogo-variant]').forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.removeAttribute('data-selected');
    });
    const selectionLabel = this.querySelector('[data-lurexia-bogo-selection]');
    if (selectionLabel) {
      const count = this.#remainingEntitlements;
      if (count > 0) {
        const template =
          count === 1
            ? this.dataset.remainingSingularLabel || 'Choose 1 free pair'
            : this.dataset.remainingPluralLabel || 'Choose [count] free pairs';
        selectionLabel.textContent = template.replace('[count]', String(count));
      } else {
        selectionLabel.textContent = '';
      }
    }
    const addButton = this.querySelector('[data-lurexia-bogo-add]');
    if (addButton instanceof HTMLButtonElement) addButton.disabled = true;
  }

  #addSelectedGift = async () => {
    if (!this.#selection || !this.#dialog || this.#giftMutationInProgress) return;
    const addButton = this.querySelector('[data-lurexia-bogo-add]');
    if (!(addButton instanceof HTMLButtonElement)) return;

    // Lock before the first request so rapid clicks cannot claim the same gift twice.
    this.#giftMutationInProgress = true;
    this.#details.setBusy(true);
    const variantId = this.#selection.variantId;
    const replacementKey = this.#replacementKey;
    const buttonText = addButton.querySelector('.button-text');
    const originalLabel = buttonText?.textContent || 'Add free pair';
    const sectionIds = this.#cartSectionIds();
    let sections;
    const deferred = CartLinesUpdateEvent.createPromise();
    addButton.disabled = true;
    addButton.setAttribute('aria-busy', 'true');
    if (buttonText) buttonText.textContent = this.dataset.addingLabel || 'Adding…';
    this.#setState('Adding your free pair…');

    this.#dialog.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'dialog',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const cart = await selectGift({
        variantId, replacementKey, selectionId: crypto.randomUUID(),
        fetchCart: () => this.#fetchCart(),
        addItem: async (item) => {
          const response = await this.#addCartItem(item, sectionIds);
          sections = response.sections;
        },
        updateQuantities: async (updates) => {
          const response = await this.#updateCart(updates, sectionIds);
          sections = response.sections;
        },
      });
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, sections, didError: false, source: 'lurexia-bogo', itemCount: 1 },
      });
      this.#announce(this.dataset.addedLabel || 'Free pair added');
      this.#remainingEntitlements = this.#offerCounts(cart).remaining;
      this.#replacementKey = '';
      this.#syncClaimWidget(cart);
      if (this.#remainingEntitlements > 0 && !replacementKey) {
        this.#clearSelection();
        this.#renderProducts();
        this.#details.showList();
        this.#setState(`Free pair added. Choose ${this.#remainingEntitlements} more free ${this.#remainingEntitlements === 1 ? "pair" : "pairs"}.`);
        this.#announce(this.dataset.addedAnotherLabel || 'Free pair added. Choose another free pair.');
      } else {
        this.#returnToCart = true;
        this.#dialog.close();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add free pair';
      try {
        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, sections, didError: true, source: 'lurexia-bogo' },
        });
        this.#syncClaimWidget(cart);
      } catch {
        deferred.resolve({ cart: null, detail: { didError: true, source: 'lurexia-bogo' } });
      }
      this.#dialog.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID', detail: { description: message } }));
      this.#setState(message);
      this.#announce(message);
    } finally {
      this.#giftMutationInProgress = false;
      this.#details.setBusy(false);
      addButton.disabled = !this.#selection;
      addButton.removeAttribute('aria-busy');
      if (buttonText) buttonText.textContent = originalLabel;
    }
  };

  async #addCartItem(item, sectionIds) {
    const response = await fetch(this.dataset.cartAddUrl || `${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [item], sections: sectionIds.join(','), sections_url: window.location.pathname }),
    });
    const payload = await response.json();
    if (!response.ok || payload.status || payload.errors) {
      throw new Error(payload.description || payload.message || 'This pair is unavailable. Please choose another pair.');
    }
    return payload;
  }

  /** @param {Record<string, any>} cart */
  async #trimExcessGifts(cart) {
    if (this.#giftMutationInProgress || this.#reconcileInProgress) return;
    const giftItems = cart.items.filter((item) => item.properties?.[PRIVATE_GIFT_PROPERTY] === 'true');
    const { qualifyingQuantity, giftQuantity } = this.#offerCounts(cart);
    let excess = Math.max(0, giftQuantity - qualifyingQuantity);
    if (excess === 0) {
      this.#syncClaimWidget(cart);
      return;
    }

    const updates = {};
    for (const item of [...giftItems].reverse()) {
      if (excess === 0) break;
      const quantityToRemove = Math.min(excess, item.quantity);
      updates[item.key] = item.quantity - quantityToRemove;
      excess -= quantityToRemove;
    }

    this.#reconcileInProgress = true;
    try {
      const sectionIds = this.#cartSectionIds();
      const response = await this.#updateCart(updates, sectionIds);
      const nextCart = await this.#fetchCart();
      this.#syncClaimWidget(nextCart);
      const deferredEventPromise = CartLinesUpdateEvent.createPromise();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(nextCart),
        detail: {
          items: nextCart.items,
          sections: response.sections,
          didError: false,
          source: 'lurexia-bogo-reconcile',
        },
      });
      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'update',
          context: 'cart',
          lines: Object.entries(updates).map(([id, quantity]) => ({ id, quantity })),
          promise: deferredEventPromise.promise,
        })
      );
    } finally {
      this.#reconcileInProgress = false;
    }
  }

  /** @param {Record<string, any>} cart */
  #offerCounts(cart) {
    return offerCounts(cart);
  }

  /** @param {Record<string, any>} cart */
  #syncClaimWidget(cart) {
    const widget = this.querySelector('[data-lurexia-bogo-claim-widget]');
    if (!(widget instanceof HTMLButtonElement)) return;

    const { remaining } = this.#offerCounts(cart);
    widget.hidden = remaining === 0 || this.#dialog?.open === true;
    if (remaining === 0) return;

    const count = widget.querySelector('[data-lurexia-bogo-claim-count]');
    if (count) count.textContent = String(remaining);

    const template =
      remaining === 1
        ? widget.dataset.widgetSingularLabel || '1 free pair ready to claim'
        : widget.dataset.widgetPluralLabel || '[count] free pairs ready to claim';
    widget.setAttribute('aria-label', template.replace('[count]', String(remaining)));
  }

  /** @param {Record<string, number>} updates @param {string[]} sectionIds */
  #updateCart(updates, sectionIds) {
    return fetch(this.dataset.cartUpdateUrl || `${window.Shopify?.routes?.root || '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        updates,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      }),
    }).then((response) => {
      if (!response.ok) throw new Error(`Cart update failed: ${response.status}`);
      return response.json();
    });
  }

  #fetchCart() {
    return fetch(`${this.dataset.cartUrl || `${window.Shopify?.routes?.root || '/'}cart`}?view=lurexia-bogo`, {
      cache: 'no-store',
      headers: { Accept: 'text/html' },
      credentials: 'same-origin',
    }).then((response) => {
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      return response.json();
    }).then((payload) => {
      if (!payload.cart?.items || !Array.isArray(payload.qualifying_product_ids)) {
        throw new Error('We could not check the free-pair offer. Please refresh and try again.');
      }
      return { ...payload.cart, bogo_qualifying_product_ids: payload.qualifying_product_ids };
    });
  }

  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component instanceof HTMLElement && component.dataset.sectionId)
      .filter(Boolean);
  }

  /** @param {string} message */
  #setState(message) {
    const state = this.querySelector('[data-lurexia-bogo-state]');
    if (!state) return;
    state.textContent = message;
    state.toggleAttribute('hidden', message === '');
  }

  /** @param {string} message */
  #announce(message) {
    const liveRegion = this.querySelector('[data-lurexia-bogo-live]');
    if (!liveRegion) return;
    liveRegion.textContent = '';
    requestAnimationFrame(() => {
      liveRegion.textContent = message;
    });
  }
}

if (!customElements.get('lurexia-bogo-offer')) {
  customElements.define('lurexia-bogo-offer', LurexiaBogoOffer);
}
